import { FormEvent, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSites } from '../../context/SiteContext';
import { useSiteSummaries } from '../../hooks/useSiteSummaries';
import { formatNumber } from '../../lib/format';
import { buildSnippet } from '../../lib/snippet';
import { fetchRemoteVisits, type SiteMetrics } from '../../lib/remote';
import type { Site } from '../../lib/types';
import Modal from '../../components/Modal';

type SortKey = 'pv' | 'ip' | 'uv';

function metric(value: number) {
  return value > 0 ? formatNumber(value) : '-';
}

function statusText(site: Site) {
  if (site.status === 'disabled') return '【停用】';
  if (site.status === 'pending') return '【等待添加网站】';
  return '';
}

export default function SitesPage() {
  const { user } = useAuth();
  const { sites, setCurrentSite, updateSite, deleteSite, clearVisits, refresh } = useSites();
  const summaries = useSiteSummaries(user?.apiToken, sites);
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [keyword, setKeyword] = useState('');
  const [page, setPage] = useState(1);
  const [sortKey, setSortKey] = useState<SortKey>('pv');
  const [sortDir, setSortDir] = useState<'desc' | 'asc'>('desc');
  const [codeSite, setCodeSite] = useState<Site | null>(null);
  const [configSite, setConfigSite] = useState<Site | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const added = params.get('added');
    if (!added) return;
    const site = sites.find((s) => s.id === added);
    if (site) setCodeSite(site);
    params.delete('added');
    setParams(params, { replace: true });
  }, [params, sites, setParams]);

  useEffect(() => {
    setPage(1);
  }, [keyword]);

  const rows = useMemo(() => {
    const empty: SiteMetrics = { pv: 0, ip: 0, uv: 0 };
    const list = sites
      .filter((s) => {
        if (!keyword) return true;
        const q = keyword.trim().toLowerCase();
        return s.domain.toLowerCase().includes(q) || s.name.toLowerCase().includes(q);
      })
      .map((site) => {
        const summary = summaries.find(
          (item) => item.trackingCode === site.trackingCode || item.id === site.id
        );
        const overview = {
          today: summary?.today || empty,
          yesterday: summary?.yesterday || empty,
        };
        return {
          site: {
            ...site,
            lastCollectAt: summary?.lastCollectAt || site.lastCollectAt,
          },
          overview,
        };
      });
    list.sort((a, b) => {
      const av = a.overview.today[sortKey];
      const bv = b.overview.today[sortKey];
      return sortDir === 'desc' ? bv - av : av - bv;
    });
    return list;
  }, [sites, summaries, keyword, sortKey, sortDir]);

  const pageSize = 8;
  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const paged = rows.slice((safePage - 1) * pageSize, safePage * pageSize);
  const totals = rows.reduce(
    (acc, r) => ({
      todayPv: acc.todayPv + r.overview.today.pv,
      todayIp: acc.todayIp + r.overview.today.ip,
      todayUv: acc.todayUv + r.overview.today.uv,
      yPv: acc.yPv + r.overview.yesterday.pv,
      yIp: acc.yIp + r.overview.yesterday.ip,
      yUv: acc.yUv + r.overview.yesterday.uv,
    }),
    { todayPv: 0, todayIp: 0, todayUv: 0, yPv: 0, yIp: 0, yUv: 0 }
  );

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const openReport = (site: Site) => {
    setCurrentSite(site.id);
    navigate('/dashboard');
  };

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <h1 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
          <span className="w-1.5 h-5 rounded-sm bg-accent-500" />
          网站列表
        </h1>
        <form
          onSubmit={(e: FormEvent) => e.preventDefault()}
          className="flex items-center gap-2"
        >
          <span className="text-sm text-foreground-600 whitespace-nowrap">域名：</span>
          <div className="flex items-center rounded-md border border-background-200 bg-background-50 overflow-hidden">
            <input
              value={keyword}
              onChange={(e) => {
                setKeyword(e.target.value);
                setPage(1);
              }}
              placeholder="输入域名搜索"
              className="w-48 px-3 py-2 text-sm focus:outline-none"
            />
            <span className="px-3 text-foreground-400">
              <i className="ri-search-line" />
            </span>
          </div>
        </form>
      </div>

      <div className="mt-4 flex justify-end">
        <Link
          to="/sites/new"
          className="inline-flex items-center px-5 py-2.5 text-sm font-semibold text-background-50 bg-accent-500 hover:bg-accent-600 rounded-md"
        >
          添加统计
        </Link>
      </div>

      <div className="mt-4 rounded-lg border border-background-200 bg-background-50 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-background-100 text-foreground-600">
              <tr>
                <th className="px-4 py-3 text-left font-medium">网站</th>
                <th className="px-4 py-3 text-left font-medium w-20" />
                <th className="px-4 py-3 text-left font-medium">
                  <button type="button" onClick={() => toggleSort('pv')} className="inline-flex items-center gap-1">
                    浏览量(PV) <i className="ri-arrow-up-down-line text-xs" />
                  </button>
                </th>
                <th className="px-4 py-3 text-left font-medium">
                  <button type="button" onClick={() => toggleSort('ip')} className="inline-flex items-center gap-1">
                    IP数 <i className="ri-arrow-up-down-line text-xs" />
                  </button>
                </th>
                <th className="px-4 py-3 text-left font-medium">
                  <button type="button" onClick={() => toggleSort('uv')} className="inline-flex items-center gap-1">
                    访客数(UV) <i className="ri-arrow-up-down-line text-xs" />
                  </button>
                </th>
                <th className="px-4 py-3 text-left font-medium">报表</th>
                <th className="px-4 py-3 text-left font-medium">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-background-100">
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-16 text-center text-foreground-500">
                    还没有站点。点击右上角「添加统计」开始接入。
                  </td>
                </tr>
              ) : (
                paged.map(({ site, overview }) => (
                  <tr key={site.id} className="align-top hover:bg-background-100/60">
                    <td className="px-4 py-4">
                      <div className="font-semibold text-foreground-950">{site.name}</div>
                      <div className="mt-1 text-xs text-foreground-500">{site.domain}</div>
                      {site.industry && (
                        <div className="mt-1 text-xs text-foreground-400">{site.industry}</div>
                      )}
                      {statusText(site) && (
                        <div className="mt-1 text-xs text-primary-700">{statusText(site)}</div>
                      )}
                      {site.lastCollectAt && (
                        <div className="mt-1 text-[11px] text-accent-700">
                          已检测到安装 · {new Date(site.lastCollectAt).toLocaleString('zh-CN')}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-4 text-xs text-foreground-500 whitespace-nowrap">
                      <div>今日:</div>
                      <div className="mt-2">昨日:</div>
                    </td>
                    <td className="px-4 py-4 font-medium text-foreground-900">
                      <div>{metric(overview.today.pv)}</div>
                      <div className="mt-2">{metric(overview.yesterday.pv)}</div>
                    </td>
                    <td className="px-4 py-4 font-medium text-foreground-900">
                      <div>{metric(overview.today.ip)}</div>
                      <div className="mt-2">{metric(overview.yesterday.ip)}</div>
                    </td>
                    <td className="px-4 py-4 font-medium text-foreground-900">
                      <div>{metric(overview.today.uv)}</div>
                      <div className="mt-2">{metric(overview.yesterday.uv)}</div>
                    </td>
                    <td className="px-4 py-4">
                      <button
                        type="button"
                        onClick={() => openReport(site)}
                        className="px-4 py-2 text-sm font-semibold text-background-50 bg-accent-500 hover:bg-accent-600 rounded-md whitespace-nowrap"
                      >
                        查看报表
                      </button>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          title="统计代码"
                          onClick={() => setCodeSite(site)}
                          className="w-9 h-9 flex items-center justify-center rounded-md border border-background-200 text-foreground-600 hover:border-accent-400 hover:text-accent-700"
                        >
                          <i className="ri-code-s-slash-line" />
                        </button>
                        <button
                          type="button"
                          title="网站配置"
                          onClick={() => setConfigSite(site)}
                          className="w-9 h-9 flex items-center justify-center rounded-md border border-background-200 text-foreground-600 hover:border-accent-400 hover:text-accent-700"
                        >
                          <i className="ri-settings-3-line" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            {rows.length > 0 && (
              <tfoot className="bg-background-100 text-sm font-semibold text-foreground-800">
                <tr>
                  <td className="px-4 py-3">合计 {rows.length} 个网站</td>
                  <td className="px-4 py-3 text-xs text-foreground-500">
                    <div>今日:</div>
                    <div className="mt-2">昨日:</div>
                  </td>
                  <td className="px-4 py-3">
                    <div>{metric(totals.todayPv)}</div>
                    <div className="mt-2">{metric(totals.yPv)}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div>{metric(totals.todayIp)}</div>
                    <div className="mt-2">{metric(totals.yIp)}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div>{metric(totals.todayUv)}</div>
                    <div className="mt-2">{metric(totals.yUv)}</div>
                  </td>
                  <td colSpan={2} />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
        {rows.length > pageSize && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-background-100 text-xs text-foreground-500">
            <span>
              第 {safePage}/{totalPages} 页
            </span>
            <div className="flex gap-2">
              <button type="button" disabled={safePage <= 1} onClick={() => setPage((p) => p - 1)} className="px-2 py-1 border rounded-md disabled:opacity-40">
                上一页
              </button>
              <button type="button" disabled={safePage >= totalPages} onClick={() => setPage((p) => p + 1)} className="px-2 py-1 border rounded-md disabled:opacity-40">
                下一页
              </button>
            </div>
          </div>
        )}
      </div>

      {codeSite && (
        <Modal title="统计代码" onClose={() => setCodeSite(null)}>
          <p className="text-sm text-foreground-600">
            把代码贴到 {codeSite.domain} 所有页面的 {'</body>'} 前。有访问后状态会自动变为启用。
          </p>
          <pre className="mt-4 overflow-x-auto rounded-md bg-foreground-950 text-background-50 p-4 text-xs">
            {buildSnippet(codeSite.trackingCode)}
          </pre>
          <div className="mt-4 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={async () => {
                await navigator.clipboard.writeText(buildSnippet(codeSite.trackingCode));
                setCopied(true);
                window.setTimeout(() => setCopied(false), 1600);
              }}
              className="px-4 py-2 text-sm font-semibold text-background-50 bg-accent-500 rounded-md"
            >
              {copied ? '已复制' : '复制代码'}
            </button>
            <button
              type="button"
              onClick={async () => {
                if (!user) return;
                const n = (await fetchRemoteVisits(codeSite.trackingCode, user.apiToken)).length;
                refresh();
                window.alert(n > 0 || codeSite.lastCollectAt ? '已检测到统计代码安装' : '尚未检测到访问，请确认代码已粘贴并打开过网站');
              }}
              className="px-4 py-2 text-sm font-semibold border border-background-300 rounded-md"
            >
              检测安装
            </button>
          </div>
        </Modal>
      )}

      {configSite && (
        <SiteConfigModal
          site={configSite}
          onClose={() => setConfigSite(null)}
          onSave={async (patch) => {
            await updateSite(configSite.id, patch);
            setConfigSite(null);
          }}
          onClear={() => {
            if (window.confirm('确定清空该站全部统计数据吗？代码不用换。')) {
              clearVisits(configSite.id);
              setConfigSite(null);
            }
          }}
          onDelete={async () => {
            if (!window.confirm(`确定删除站点「${configSite.name}」吗？`)) return;
            try {
              await deleteSite(configSite.id);
              setConfigSite(null);
            } catch (err) {
              window.alert(err instanceof Error ? err.message : '删除失败');
            }
          }}
        />
      )}
    </div>
  );
}

function SiteConfigModal({
  site,
  onClose,
  onSave,
  onClear,
  onDelete,
}: {
  site: Site;
  onClose: () => void;
  onSave: (patch: Partial<Pick<Site, 'name' | 'domain' | 'status' | 'industry'>>) => void | Promise<void>;
  onClear: () => void;
  onDelete: () => void;
}) {
  const [name, setName] = useState(site.name);
  const [domain, setDomain] = useState(site.domain);
  const [industry, setIndustry] = useState(site.industry || '');
  const [status, setStatus] = useState(site.status === 'disabled' ? 'disabled' : 'active');
  const [error, setError] = useState('');

  return (
    <Modal title="网站配置" onClose={onClose}>
      <div className="flex flex-col gap-3">
        <label className="text-sm">
          <span className="text-foreground-700">网站名称</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="mt-1 w-full px-3 py-2 rounded-md border border-background-200"
          />
        </label>
        <label className="text-sm">
          <span className="text-foreground-700">域名</span>
          <input
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            className="mt-1 w-full px-3 py-2 rounded-md border border-background-200"
          />
        </label>
        <label className="text-sm">
          <span className="text-foreground-700">行业</span>
          <input
            value={industry}
            onChange={(e) => setIndustry(e.target.value)}
            placeholder="例如：博客 / 电商 / 工具"
            className="mt-1 w-full px-3 py-2 rounded-md border border-background-200"
          />
        </label>
        <label className="text-sm">
          <span className="text-foreground-700">统计状态</span>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as 'active' | 'disabled')}
            className="mt-1 w-full px-3 py-2 rounded-md border border-background-200"
          >
            <option value="active">启用</option>
            <option value="disabled">停用（不再接收统计）</option>
          </select>
        </label>
      </div>
      {error && <p className="mt-3 text-sm text-primary-700">{error}</p>}
      <div className="mt-5 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={async () => {
            setError('');
            try {
              await onSave({ name, domain, industry, status: status as Site['status'] });
            } catch (err) {
              setError(err instanceof Error ? err.message : '保存失败');
            }
          }}
          className="px-4 py-2 text-sm font-semibold text-background-50 bg-accent-500 rounded-md"
        >
          保存
        </button>
        <button type="button" onClick={onClear} className="px-4 py-2 text-sm border border-background-300 rounded-md">
          初始化数据
        </button>
        <button type="button" onClick={onDelete} className="px-4 py-2 text-sm text-primary-700">
          删除网站
        </button>
      </div>
    </Modal>
  );
}
